/**
 * A stopwatch that only runs while the app is in the foreground (SE-7443).
 * @remarks
 * ## Why this exists
 *
 * SE-7078 gave the AskGemini chat stream a silence budget built on a plain
 * `setTimeout`. React Native suspends JS while the app is backgrounded and
 * flushes expired timers on resume, so a user who backgrounds the app
 * mid-answer for longer than the budget comes back to an already-expired
 * watchdog — and a stream that is still perfectly healthy gets torn down with
 * "the assistant stopped responding".
 *
 * The fix is not a longer budget: that would hide the false failure behind a
 * bigger number while making a genuinely dead stream take proportionally longer
 * to detect. The fix is to measure the right quantity. What the silence budget
 * bounds is *how long the user has been watching a stream produce nothing*, and
 * time spent with the app suspended is not that.
 *
 * ## Why the check happens when the timer FIRES, not only when it is re-armed
 *
 * The obvious shape — "re-arm the timer on AppState → `active`" — has a race
 * SE-7443 calls out: on resume the JS queue holds both the expired timer and
 * the AppState notification, with no guaranteed ordering between them. If the
 * timer wins, re-arming afterwards is too late; the stream is already gone.
 *
 * So this clock is designed to be *consulted by the timer callback itself*.
 * {@link foregroundElapsed} subtracts a background window that is still open,
 * which makes the answer correct in both orderings:
 *
 * - timer first — the window is still open, elapsed foreground time is small,
 *   the watchdog re-arms for the remainder instead of failing;
 * - AppState first — the window is closed and its duration banked, elapsed
 *   foreground time is the same small number, same outcome.
 *
 * Re-arming on foreground is still done at the call site, but as a belt-and-
 * braces guarantee that a live timer exists — not as the mechanism that makes
 * the decision correct.
 *
 * ## Shape
 *
 * A frozen value plus transformations, rather than an object with internal
 * mutable state: the repo's functional lint forbids the latter, and an
 * immutable clock is easier to assert against besides. Callers keep the
 * current value in a ref and replace it — `useSSE` does exactly that.
 *
 * Every function takes `now` rather than reading the clock itself, so callers
 * pass `Date.now()` and tests pass whatever they like. `Date.now()` is the
 * right source in production precisely because it keeps advancing while JS is
 * suspended — that is the elapsed real time this clock needs to discount.
 * @see {@link https://linear.app/gemini-sports/issue/SE-7443}
 * @see {@link https://linear.app/gemini-sports/issue/SE-7078}
 */

/**
 * A stopwatch reading whose elapsed time excludes any period spent
 * backgrounded.
 */
export interface ForegroundClock {
  /** Wall-clock time the current measurement started from. */
  readonly startedAt: number;
  /** Backgrounded milliseconds already banked since {@link ForegroundClock.startedAt}. */
  readonly backgroundedMs: number;
  /** Start of a background window that has not been closed yet, or null. */
  readonly backgroundedAt: number | null;
}

/**
 * Duration of a background window that has not been closed yet.
 * @param clock - Clock to inspect
 * @param at - Current wall-clock time in ms
 * @returns Milliseconds spent in the currently-open window, or 0 when
 * foregrounded
 */
const openWindowMs = (clock: ForegroundClock, at: number): number =>
  clock.backgroundedAt === null ? 0 : Math.max(0, at - clock.backgroundedAt);

/**
 * Starts a foreground stopwatch.
 * @param now - Wall-clock time in ms to measure from
 * @returns A clock running from `now` with nothing discounted yet
 */
export const createForegroundClock = (now: number): ForegroundClock => ({
  startedAt: now,
  backgroundedMs: 0,
  backgroundedAt: null,
});

/**
 * Restarts the stopwatch from zero.
 * @param clock - Clock to restart
 * @param now - Wall-clock time in ms to measure from
 * @returns A clock measuring from `now`
 * @remarks
 * An open background window is restarted rather than closed. A frame arriving
 * while backgrounded is unusual but not impossible — the OS grants a short tail
 * of execution — and carrying the window's original start across the reset
 * would discount the same period twice.
 */
export const resetForegroundClock = (
  clock: ForegroundClock,
  now: number
): ForegroundClock => ({
  startedAt: now,
  backgroundedMs: 0,
  backgroundedAt: clock.backgroundedAt === null ? null : now,
});

/**
 * Records that the app left the foreground.
 * @param clock - Clock to update
 * @param now - Wall-clock time in ms of the transition
 * @returns A clock with a background window open
 * @remarks Idempotent, because iOS emits `inactive` and then `background` for a
 * single trip away; starting the window at the second one would under-discount
 * the sleep.
 */
export const markBackgrounded = (
  clock: ForegroundClock,
  now: number
): ForegroundClock =>
  clock.backgroundedAt === null ? { ...clock, backgroundedAt: now } : clock;

/**
 * Records that the app returned to the foreground.
 * @param clock - Clock to update
 * @param now - Wall-clock time in ms of the transition
 * @returns A clock with the background window closed and banked
 * @remarks Idempotent: a call while already foregrounded changes nothing.
 */
export const markForegrounded = (
  clock: ForegroundClock,
  now: number
): ForegroundClock =>
  clock.backgroundedAt === null
    ? clock
    : {
        startedAt: clock.startedAt,
        backgroundedMs: clock.backgroundedMs + openWindowMs(clock, now),
        backgroundedAt: null,
      };

/**
 * Milliseconds of FOREGROUND time since the clock last started.
 * @param clock - Clock to read
 * @param now - Current wall-clock time in ms
 * @returns Elapsed time with every backgrounded window discounted, **including
 * one that is still open** — which is what makes this safe to call from a timer
 * callback flushed on resume, before anything has said the app is back
 * @remarks Floored at zero: `Date.now()` is not monotonic and an NTP correction
 * must not read as negative silence.
 */
export const foregroundElapsed = (
  clock: ForegroundClock,
  now: number
): number =>
  Math.max(
    0,
    now - clock.startedAt - clock.backgroundedMs - openWindowMs(clock, now)
  );
